import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_FIREWALL_ADVANCED_RULES_GRID } from '../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  nameCellCustomRenderer,
  networkCellCustomRenderer,
  PREDEFINED_FILTERS_KEYS,
} from './grid-config';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { getApplicationControlSearchableKeywords } from '../../../../utils/uiSettings/applicationControl/application-control.values';
import {
  FirewallAdvancedRuleFormValueWithAdditionalFrontendFields,
  GzFirewallAdvancedRulesGridDataProvider,
} from './GzFirewallAdvancedRulesGridDataProvider';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzButtonWebComponent } from '../../../../../../../ui/gzButton';
import { GzFirewallAdvancedRulesFormWebComponent } from '../helpers/rulesItemForm';
import { ValidationResult } from '../../../../../../../ui/gzForm/formField/types/form-validator';
import { isBrokenRule, validateDuplicates } from '../utils/validators';
import {
  FirewallAdvancedRule,
  FirewallAdvancedRuleFormValue,
  FirewallAdvancedRuleType,
} from '@nirvana/shared/dtos/types/policies/common';
import { OverlayPositionX } from '../../../../../../../ui/gzSelect/types';
import { GzPolicyUiSettingsImportDialog } from '../../../../../../common/policyPanel/uiSettings/dialogs/import/GzPolicyUiSettingsImportDialog';
import {
  FirewallRulesApiClients,
  ImportFileApiClients,
  PolicyDependenciesContainer,
} from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { getSanitizedItemWithoutAdditionalFrontendFields } from '../utils/converters';
import { selectPoliciesState } from '../../../../redux/slice';
import { store } from '@nirvana/redux/store';
import { getNavigationHeaderSlice } from '../../../../redux/navigationHeaderSlice/actions.navigation';
import { GzDialog } from '../../../../../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../../../../../ui/gzDialog';
import { v4 as uuidv4 } from 'uuid';
import { ButtonFontSizes, ButtonHeights, ButtonTypes, ButtonColors } from '../../../../../../../ui/gzButton/types';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { showErrorToast } from '../../../../../../../ui/gzToastNotification';

@customElement(WEB_COMPONENT_POLICY_PANEL_FIREWALL_ADVANCED_RULES_GRID)
export class GzFirewallAdvancedRulesGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  FirewallAdvancedRule,
  FirewallAdvancedRuleFormValue,
  GzFirewallAdvancedRulesGridDataProvider
> {
  protected clients = PolicyDependenciesContainer.getInstance().resolveDependency(
    'FirewallRules'
  ) as FirewallRulesApiClients;
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected columnRenderers = {
    [ColumnKeys.NAME]: nameCellCustomRenderer.bind(this, this.onRuleNameClick.bind(this)),
    [ColumnKeys.NETWORK]: networkCellCustomRenderer,
  };
  protected showHeaderDefaultActions = true;
  protected showHeaderCustomActionsInsideGrid = true;

  protected searchableKeywords = getApplicationControlSearchableKeywords();

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasSelectableRows = true;

  protected hasPriorityColumn = true;
  protected hasMoveToTopAction = true;
  protected hasEnableInlineRow = true;
  protected hasDisableInlineRow = true;

  protected translations = {
    editRow: translateFn('Edit priority'),
    deleteRow: translateFn('Delete'),
    moveToTop: translateFn('Move to top'),
    enableRow: translateFn('Enable'),
    disableRow: translateFn('Disable'),
  };

  private addButtonActions = [
    { title: translateFn('Application'), value: FirewallAdvancedRuleType.APPLICATION },
    { title: translateFn('Connection'), value: FirewallAdvancedRuleType.CONNECTION },
  ];

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;
  @query('#export-csv-button')
  protected exportButton: GzButtonWebComponent;
  @query('#enable-button')
  protected enableButton: GzButtonWebComponent;
  @query('#disable-button')
  protected disableButton: GzButtonWebComponent;

  protected getDataProvider(): GzFirewallAdvancedRulesGridDataProvider {
    return new GzFirewallAdvancedRulesGridDataProvider(this.initialGridItems);
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <div style="display: flex; align-items: center;">
        <gz-select
          id="add-button-select"
          .options="${this.addButtonActions}"
          @selectionChanged="${this.onAddButtonClick.bind(this)}"
          overlayPositionX="${OverlayPositionX.LEFT}"
        >
          <gz-button id="add-button" type="${ButtonTypes.OUTLINE}">
            ${translateFn('Add')} <gz-icon name="ds-icon-textbox_decrease" color="primary" size="20"></gz-icon>
          </gz-button>
        </gz-select>

        <gz-tooltip
          id="tooltip"
          class="tooltip"
          tooltipText="${translateFn('Export rules (available only for saved policies)')}"
        >
          <gz-button
            id="export-csv-button"
            type="${ButtonTypes.QUIET}"
            color="${ButtonColors.SECONDARY}"
            @click="${this.exportRules}"
            ?disabled="${this.isExportButtonDisabled()}"
            >${translateFn('Export')}</gz-button
          >
        </gz-tooltip>

        <gz-button
          id="import-csv-button"
          type="${ButtonTypes.QUIET}"
          color="${ButtonColors.SECONDARY}"
          @click="${this.importRules}"
          >${translateFn('Import')}</gz-button
        >

        <gz-button
          id="enable-button"
          type="${ButtonTypes.QUIET}"
          color="${ButtonColors.SECONDARY}"
          disabled
          @click="${this.changeRulesEnabledStateOfSelectedItems.bind(this, true)}"
          >${translateFn('Enable')}
        </gz-button>

        <gz-button
          id="disable-button"
          type="${ButtonTypes.QUIET}"
          color="${ButtonColors.SECONDARY}"
          disabled
          @click="${this.changeRulesEnabledStateOfSelectedItems.bind(this, false)}"
          >${translateFn('Disable')}
        </gz-button>

        <gz-button
          id="delete-button"
          type="outline"
          disabled
          color="negative"
          @click="${this.onDeleteSelectedItemsWithDialog.bind(
            this,
            translateFn('Delete rules?'),
            translateFn('You are about to delete the selected rules. This action cannot be undone.')
          )}"
          >${translateFn('Delete')}
        </gz-button>
      </div>
    `;
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.LIGHT}">${translateFn('No rules')}</gz-text>

      <gz-select
        style="padding-top: 5px;"
        id="add-button-empty-state"
        .options="${this.addButtonActions}"
        closeOnMouseLeave
        @selectionChanged="${this.onAddButtonClick.bind(this)}"
        overlayPositionX="${OverlayPositionX.LEFT}"
      >
        <gz-button
          type="${ButtonTypes.QUIET}"
          buttonHeight="${ButtonHeights.SMALL}"
          buttonFontSize="${ButtonFontSizes.SMALL}"
          >${translateFn('Adjust your filters or start adding rules.')}
        </gz-button>
      </gz-select>
    `;
  }

  public reset() {
    this.gridComponent.controller?.discardViewChanges();
  }

  public setExportButtonDisabledState() {
    this.exportButton.disabled = this.isExportButtonDisabled();
  }

  private isExportButtonDisabled() {
    const state = selectPoliciesState(store.getState());
    const navigationHeaderSlice = getNavigationHeaderSlice(state);
    return navigationHeaderSlice.context === 'clone';
  }

  private exportRules() {
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `
      <div>
      <p>${translateFn('Firewall rules will be saved locally in CSV format.')}</p>
              <gz-info-box id="export-warning" severity="warning" style="  padding-left: 0px; --info-box-width: 100%">
                ${translateFn('Make sure to save the policy to export the latest rules.')}
              </gz-info-box>
      </div>
    `;

    const footer = document.createElement('div');
    footer.innerHTML = `
      <gz-button id="buttonOne" type="full" color="primary">${translateFn('Export')}</gz-button>
      <gz-button id="buttonTwo" type="outline" color="secondary">${translateFn('Cancel')}</gz-button>
    `;

    const footerBtnOne = footer.querySelector('#buttonOne');
    const footerBtnTwo = footer.querySelector('#buttonTwo');

    const dialogConfigs: GzDialog = {
      headline: translateFn('Export rules'),
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(
      dialogConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );

    if (footerBtnOne) {
      footerBtnOne.addEventListener('click', () => {
        this.clients.ExportFirewallRules(this.policyId);
        dialog.close();
      });
    }

    if (footerBtnTwo) {
      footerBtnTwo.addEventListener('click', () => {
        dialog.dismiss();
      });
    }
  }

  private importRules() {
    const importFn = async (file: File) => {
      const importClient = PolicyDependenciesContainer.getInstance().resolveDependency(
        'ImportFile'
      ) as ImportFileApiClients;

      return await importClient.ConvertFirewallRulesCsvToJson(file);
    };

    const dialog = new GzPolicyUiSettingsImportDialog(importFn, this.appendExclusionsItemsToGrid.bind(this), {
      title: translateFn('Import rules'),
      fileInput: {
        label: translateFn('Rules file'),
      },
      validationMessages: {
        valid: translateFn('Rules successfully imported.'),
      },
    });
    dialog.show();
  }

  private appendExclusionsItemsToGrid(items: FirewallAdvancedRule[]): {
    valid: boolean;
    rowWithErrors: number[];
  } {
    // already validated in backend, so we assume that all items are valid
    (this.gridComponent.dataProvider as GzFirewallAdvancedRulesGridDataProvider).items = items.map(
      (item: FirewallAdvancedRule) => {
        return {
          ...item,
          ruleId: uuidv4(),
          type: parseInt(String(item.type), 10),
        };
      }
    );

    this.onGridItemsChange();
    this.gridComponent.controller?.handleGridItemsLoad();

    return { valid: true, rowWithErrors: [] };
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: FirewallAdvancedRuleFormValue): ValidationResult {
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzFirewallAdvancedRulesGridDataProvider).items
    );
  }

  protected sanitizeGridItemBeforeEdit(
    gridItem: FirewallAdvancedRuleFormValueWithAdditionalFrontendFields
  ): FirewallAdvancedRuleFormValue {
    return getSanitizedItemWithoutAdditionalFrontendFields(gridItem);
  }

  protected validateGridItemBeforeEdit(
    oldItem: FirewallAdvancedRuleFormValue,
    newItem: FirewallAdvancedRuleFormValue
  ): ValidationResult {
    // skip if item name is not changed
    if (newItem.details.name === oldItem.details.name) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);

    const isDisabled = !event.detail.selectionStoreData.length;
    this.deleteButton.disabled = isDisabled;
    this.enableButton.disabled = isDisabled;
    this.disableButton.disabled = isDisabled;
  }

  private onAddButtonClick(event: CustomEvent): void {
    const maxPriority = (this.gridComponent.dataProvider as GzFirewallAdvancedRulesGridDataProvider).items.length + 1;
    const addRuleCallback = (formValue: FirewallAdvancedRuleFormValue): ValidationResult =>
      this.onNewGridItemAdd(formValue, formValue.defaultPositionInGrid);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzFirewallAdvancedRulesFormWebComponent(maxPriority, event.detail.selected),
      strings: {
        title:
          event.detail.selected === FirewallAdvancedRuleType.CONNECTION
            ? translateFn('Add connection rule')
            : translateFn('Add application rule'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  private onRuleNameClick(item: FirewallAdvancedRuleFormValue): void {
    const maxPriority = (this.gridComponent.dataProvider as GzFirewallAdvancedRulesGridDataProvider).items.length;
    const addRuleCallback = (formValue: FirewallAdvancedRuleFormValue): ValidationResult =>
      this.onEditGridItemFromPopup(formValue, item);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzFirewallAdvancedRulesFormWebComponent(maxPriority, item.ruleType, item),
      strings: {
        title:
          item.ruleType === FirewallAdvancedRuleType.CONNECTION
            ? translateFn('Edit connection rule')
            : translateFn('Edit application rule'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected onEnableGridItem(event: CustomEvent) {
    this.changeRuleEnabledState(event.detail.rowData.item.defaultPositionInGrid, true);
    this.onGridItemsChange();
    this.gridComponent.controller?.handleGridItemsLoad();
  }

  protected onDisableGridItem(event: CustomEvent) {
    this.changeRuleEnabledState(event.detail.rowData.item.defaultPositionInGrid, false);
    this.onGridItemsChange();
    this.gridComponent.controller?.handleGridItemsLoad();
  }

  private changeRulesEnabledStateOfSelectedItems(enabled: boolean) {
    if (!this.hasSelectableRows) {
      return;
    }

    this.selectedItemsGridData.forEach(position => this.changeRuleEnabledState(position, enabled));
    this.onGridItemsChange();
    this.gridComponent.controller?.handleGridItemsLoad(true, true);
  }

  private changeRuleEnabledState(position: number, enabled: boolean): void {
    const dataProvider = this.gridComponent.dataProvider as GzFirewallAdvancedRulesGridDataProvider;
    const item = dataProvider.items[position - 1];

    if (isBrokenRule(item) && enabled) {
      showErrorToast(translateFn('Cannot enable an invalid rule.'));
      return;
    }

    // skip if item is already in the desired state
    if (item.enabled === enabled) {
      return;
    }

    // update item
    dataProvider.updateItem(position, {
      ...item,
      enabled: enabled,
    });
  }
}
